import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Flame, Loader2, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function Login() {
  const { login, register, forgotPassword, formatApiErrorDetail, user } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "forgot") {
        await forgotPassword(email);
        setSent(true);
        return;
      }
      if (mode === "login") await login(email, password);
      else await register(email, password, name);
      navigate("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const demoLogin = async () => {
    setLoading(true);
    try {
      await login("demo@mealcost.app", "demo1234");
      navigate("/");
    } catch (err) {
      toast.error("Demo login failed");
    } finally {
      setLoading(false);
    }
  };

  const goMode = (m) => { setMode(m); setSent(false); };

  const heading = mode === "login" ? "Welcome back" : mode === "register" ? "Create your account" : "Reset your password";
  const sub = mode === "login" ? "Log in to track your meal costs."
    : mode === "register" ? "Start tracking in seconds."
    : "Enter your email and we'll send you a reset link.";

  return (
    <div className="flex min-h-screen">
      <div className="hidden w-1/2 flex-col justify-between p-12 text-white lg:flex"
        style={{ background: "linear-gradient(135deg,#FA7247 0%,#F59E0B 100%)" }}>
        <div className="flex items-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20">
            <Flame className="h-6 w-6" />
          </div>
          <span className="font-display text-xl font-extrabold">Meal Cost</span>
        </div>
        <div>
          <h1 className="font-display text-4xl font-extrabold leading-tight lg:text-5xl">
            Know exactly what<br />your meals cost.
          </h1>
          <p className="mt-4 max-w-md text-lg text-white/85">
            Track groceries, build meals, and see cost per serving, per day, per month — instantly.
          </p>
        </div>
        <p className="text-sm text-white/70">Per serving · Per meal · Per day · Per month</p>
      </div>

      <div className="flex w-full items-center justify-center px-6 py-12 lg:w-1/2">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="mb-8 flex items-center gap-2 lg:hidden">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand text-white">
              <Flame className="h-6 w-6" />
            </div>
            <span className="font-display text-xl font-extrabold">Meal Cost</span>
          </div>

          <h2 className="font-display text-2xl font-extrabold tracking-tight">{heading}</h2>
          <p className="mt-1 text-sm text-slate-500">{sub}</p>

          {mode === "forgot" && sent ? (
            <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-center" data-testid="forgot-sent">
              <MailCheck className="mx-auto h-8 w-8 text-emerald-600" />
              <p className="mt-2 font-semibold text-emerald-800">Check your inbox</p>
              <p className="mt-1 text-sm text-emerald-700">If an account exists for {email}, a reset link is on its way. It expires in 1 hour.</p>
              <button onClick={() => goMode("login")} data-testid="back-to-login"
                className="mt-4 text-sm font-semibold text-brand hover:underline">Back to log in</button>
            </div>
          ) : (
            <>
              <form onSubmit={submit} className="mt-6 space-y-4">
                {mode === "register" && (
                  <div>
                    <Label htmlFor="name">Name</Label>
                    <Input id="name" data-testid="register-name-input" value={name}
                      onChange={(e) => setName(e.target.value)} placeholder="Your name" className="mt-1.5" />
                  </div>
                )}
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" required data-testid="login-email-input" value={email}
                    onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="mt-1.5" />
                </div>
                {mode !== "forgot" && (
                  <div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password">Password</Label>
                      {mode === "login" && (
                        <button type="button" onClick={() => goMode("forgot")} data-testid="forgot-password-link"
                          className="text-xs font-semibold text-brand hover:underline">Forgot password?</button>
                      )}
                    </div>
                    <Input id="password" type="password" required data-testid="login-password-input" value={password}
                      onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" className="mt-1.5" />
                  </div>
                )}
                <Button type="submit" disabled={loading} data-testid="login-submit-button"
                  className="w-full rounded-full bg-brand py-6 text-base font-semibold hover:bg-brand-hover">
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" />
                    : mode === "login" ? "Log in" : mode === "register" ? "Create account" : "Send reset link"}
                </Button>
              </form>

              {mode === "login" && (
                <button onClick={demoLogin} data-testid="demo-login-button"
                  className="mt-3 w-full rounded-full border border-slate-200 py-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50">
                  Try the demo account
                </button>
              )}

              <p className="mt-6 text-center text-sm text-slate-500">
                {mode === "forgot" ? (
                  <button onClick={() => goMode("login")} data-testid="back-to-login" className="font-semibold text-brand hover:underline">Back to log in</button>
                ) : (
                  <>
                    {mode === "login" ? "New here? " : "Already have an account? "}
                    <button data-testid="toggle-auth-mode" onClick={() => goMode(mode === "login" ? "register" : "login")}
                      className="font-semibold text-brand hover:underline">
                      {mode === "login" ? "Create an account" : "Log in"}
                    </button>
                  </>
                )}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
